import { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { validateEmail, normalizeEmail } from '../utils/emailValidation';

const API = process.env.REACT_APP_BACKEND_URL;

// Paths where NO popup should show (dashboards, checkouts, etc.)
const EXCLUDED_PATHS = [
  '/admin', '/login', '/ai-visibility-trial',
  '/warm-handoffs', '/gift-portfolio', '/es/regalo-portafolio',
  '/partner/hq', '/partner/install',
  '/affiliate/dashboard',
  '/hosting', '/client-portal', '/client', '/ppc-portal', '/client-pay',
  '/2026-kentucky-derby', '/contest/enter', '/analyze',
];

const ACCENT_MAP = {
  indigo: 'from-indigo-600 to-indigo-700',
  emerald: 'from-emerald-600 to-emerald-700',
  amber: 'from-amber-500 to-amber-600',
  rose: 'from-rose-600 to-pink-700',
  blue: 'from-blue-600 to-blue-700',
  purple: 'from-purple-600 to-fuchsia-700',
  slate: 'from-slate-800 to-slate-900',
};

function getVisitorId() {
  let id = localStorage.getItem('visitor_id');
  if (!id) {
    id = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('visitor_id', id);
  }
  return id;
}

function isReturning() {
  return localStorage.getItem('visitor_returning') === '1';
}

export default function DynamicPopup() {
  const [popup, setPopup] = useState(null);
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const impressionLogged = useRef(false);

  // Language detection from path
  const isEs = typeof window !== 'undefined' && window.location.pathname.startsWith('/es');
  const lang = isEs ? 'es' : 'en';

  // Check if popup should be suppressed on this page
  const pathExcluded = () => {
    const p = window.location.pathname;
    return EXCLUDED_PATHS.some((x) => p.startsWith(x));
  };

  const sessionShown = () => sessionStorage.getItem('dynamic_popup_shown') === '1';

  // Fetch active popup from backend
  useEffect(() => {
    if (pathExcluded() || sessionShown()) return;
    const vid = getVisitorId();
    const returning = isReturning();
    fetch(`${API}/api/popup/active?visitor_id=${encodeURIComponent(vid)}&is_returning=${returning}`)
      .then((r) => r.json())
      .then((j) => {
        if (j && j.popup) setPopup(j.popup);
      })
      .catch(() => {});
    // Mark as returning for future sessions
    localStorage.setItem('visitor_returning', '1');
    // eslint-disable-next-line
  }, []);

  // Trigger logic — show after 8s dwell OR on exit intent
  useEffect(() => {
    if (!popup) return;
    let timer;
    const trigger = () => {
      if (sessionShown()) return;
      setShow(true);
      sessionStorage.setItem('dynamic_popup_shown', '1');
      // Suppress legacy popups for this session so only one shows at a time
      sessionStorage.setItem('popup_shown', '1');        // ExitIntentPopup
      sessionStorage.setItem('entryPopupShown', 'true'); // SmartPopup entry
      sessionStorage.setItem('exitPopupShown', 'true');  // SmartPopup exit
      // Log impression (only once per visit)
      if (!impressionLogged.current) {
        impressionLogged.current = true;
        fetch(`${API}/api/popup/impression`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ popup_id: popup.id, visitor_id: getVisitorId(), path: window.location.pathname }),
        }).catch(() => {});
      }
    };
    // 8-second dwell timer
    timer = setTimeout(trigger, 8000);
    // Desktop exit intent
    const onMouseLeave = (e) => { if (e.clientY <= 0) trigger(); };
    document.addEventListener('mouseleave', onMouseLeave);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [popup]);

  const content = popup?.content?.[lang] || popup?.content?.en || {};
  const destination = popup?.destination || {};

  const close = () => setShow(false);

  const logClick = () => {
    if (!popup) return;
    fetch(`${API}/api/popup/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ popup_id: popup.id, visitor_id: getVisitorId(), path: window.location.pathname }),
    }).catch(() => {});
  };

  const handleCta = () => {
    logClick();
    if (destination.type === 'external' && destination.url) {
      window.open(destination.url, '_blank', 'noopener');
      close();
    } else if (destination.type === 'url' && destination.url) {
      window.location.href = destination.url;
    }
    // form type: stay and show fields
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Validate required fields
    for (const f of (destination.form_fields || [])) {
      if (f.required && !(formData[f.key] || '').trim()) {
        setError(`${f.label} is required`);
        return;
      }
      if (f.type === 'email' && formData[f.key]) {
        const err = validateEmail(formData[f.key]);
        if (err) { setError(err); return; }
      }
    }
    setSubmitting(true);
    try {
      const payload = { ...formData };
      // Normalize email if present
      (destination.form_fields || []).forEach((f) => {
        if (f.type === 'email' && payload[f.key]) payload[f.key] = normalizeEmail(payload[f.key]);
      });
      const r = await fetch(`${API}/api/popup/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          popup_id: popup.id,
          fields: payload,
          visitor_id: getVisitorId(),
          path: window.location.pathname,
          language: lang,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Submit failed');
      setSubmitted(true);
      if (j.redirect) {
        setTimeout(() => { window.location.href = j.redirect; }, 1500);
      }
    } catch (e2) {
      setError(e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!show || !popup) return null;

  const accent = content.accent_color || 'indigo';
  const gradient = ACCENT_MAP[accent] || ACCENT_MAP.indigo;
  const isForm = destination.type === 'form';

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={close}
      data-testid="dynamic-popup"
    >
      <div
        className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-md w-full relative animate-in slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid={`dynamic-popup-${popup.id}`}
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-black/20 hover:bg-black/40 text-white z-10"
          aria-label="Close"
          data-testid="dynamic-popup-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className={`bg-gradient-to-br ${gradient} text-white p-6 text-center`}>
          {content.emoji_icon && <div className="text-5xl mb-3">{content.emoji_icon}</div>}
          <h2 className="text-2xl font-bold leading-tight" data-testid="dynamic-popup-headline">
            {content.headline}
          </h2>
          {content.subheadline && (
            <p className="text-sm opacity-95 mt-2 font-medium">{content.subheadline}</p>
          )}
        </div>

        <div className="p-6 space-y-4">
          {content.description && (
            <p className="text-sm text-slate-700 text-center leading-relaxed">
              {content.description}
            </p>
          )}

          {submitted ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center" data-testid="dynamic-popup-success">
              <p className="text-emerald-800 font-semibold">
                {lang === 'es' ? '¡Gracias! Revisa tu correo.' : 'Thanks! Check your inbox.'}
              </p>
            </div>
          ) : isForm ? (
            <form onSubmit={handleFormSubmit} className="space-y-3">
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                style={{ position: 'absolute', left: '-9999px' }}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
              {(destination.form_fields || []).map((f) => (
                <div key={f.key}>
                  <input
                    type={f.type || 'text'}
                    placeholder={f.label + (f.required ? ' *' : '')}
                    value={formData[f.key] || ''}
                    onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required={f.required}
                    data-testid={`dynamic-popup-field-${f.key}`}
                  />
                </div>
              ))}
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className={`w-full py-3 rounded-lg text-white font-semibold bg-gradient-to-r ${gradient} hover:opacity-90 disabled:opacity-50 transition-opacity`}
                data-testid="dynamic-popup-submit"
              >
                {submitting ? '...' : (content.cta || (lang === 'es' ? 'Enviar' : 'Submit'))}
              </button>
            </form>
          ) : (
            <button
              onClick={handleCta}
              className={`w-full py-3 rounded-lg text-white font-semibold bg-gradient-to-r ${gradient} hover:opacity-90 transition-opacity`}
              data-testid="dynamic-popup-cta"
            >
              {content.cta}
            </button>
          )}

          <p className="text-[10px] text-slate-400 text-center">
            {lang === 'es' ? 'Sin spam. Cierra cuando quieras.' : 'No spam. Close anytime.'}
          </p>
        </div>
      </div>
    </div>
  );
}
